import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StaffNotFound from '../StaffNotFound';
import { FEATURE_GROUPS, featureByKey } from '../../../data/featureFlags';
import { getFeatureFlags, setFeatureFlag } from '../../../services/api';

const shadow = { boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' };

function Panel({ title, children }) {
    return (
        <section className="border border-gray-300 bg-offwhite" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}>
            <header className="flex items-center justify-between gap-2 bg-maroon px-3 py-[6px]">
                <h3 className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white">{title}</h3>
            </header>
            <div className="p-3">{children}</div>
        </section>
    );
}

function FeatureRow({ row, saving, onToggle }) {
    const meta = featureByKey(row.feature);
    const busy = saving === row.feature;

    return (
        <div className="flex flex-col gap-2 rounded bg-gray-200 px-3 py-[10px] sm:flex-row sm:items-center" style={shadow}>
            <div className="min-w-0 flex-1">
                <span className="block font-['Jersey_10'] text-[17px] uppercase tracking-[1px] text-gray-800">
                    {meta?.label || row.label}
                </span>
                <span className="block text-[11px] leading-[15px] text-gray-600">
                    {(meta?.paths || []).join(', ')}
                    {meta?.note ? `. ${meta.note}` : ''}
                </span>
            </div>

            <span
                className={`shrink-0 rounded px-3 py-1 font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-white ${
                    row.enabled ? 'bg-green-600' : 'bg-red-600'
                }`}
            >
                {row.enabled ? 'On' : 'Off'}
            </span>

            <button
                type="button"
                onClick={() => onToggle(row.feature, !row.enabled)}
                disabled={busy}
                className={`w-[120px] shrink-0 rounded px-3 py-2 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-all ${
                    row.enabled ? 'bg-red-600 hover:opacity-90' : 'bg-green-600 hover:opacity-90'
                } ${busy ? 'cursor-not-allowed opacity-50' : ''}`}
                style={shadow}
            >
                {busy ? 'Saving...' : row.enabled ? 'Switch off' : 'Switch on'}
            </button>
        </div>
    );
}

function Features({ userData }) {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await getFeatureFlags();
            if (res?.success) setFeatures(res.features || []);
        } catch (error) {
            toast.error('Failed to load feature switches');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const row of features) {
            const group = featureByKey(row.feature)?.group || 'Other';
            if (!map.has(group)) map.set(group, []);
            map.get(group).push(row);
        }

        const order = [...FEATURE_GROUPS, 'Other'];
        return order
            .filter(group => map.has(group))
            .map(group => ({ group, rows: map.get(group) }));
    }, [features]);

    const toggle = async (feature, enabled) => {
        setSaving(feature);
        try {
            const res = await setFeatureFlag(feature, enabled);
            if (res?.success) {
                setFeatures(prev => prev.map(f => (f.feature === feature ? { ...f, enabled: res.enabled } : f)));
                toast.success(res.message, { autoClose: 3000 });
            }
        } catch (error) {
            toast.error('Failed to save the switch');
        } finally {
            setSaving('');
        }
    };

    if (!userData || !userData.hasAdminRole) return <StaffNotFound inline />;

    if (loading) {
        return (
            <Panel title="Feature switches">
                <p className="text-[13px] text-gray-600">Loading...</p>
            </Panel>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <Panel title="What switching off does">
                <ul className="flex flex-col gap-1 text-[13px] text-gray-700">
                    <li>The navigation link disappears for members and logged out visitors.</li>
                    <li>Admins keep the link, greyed out, so it is clear the feature is off.</li>
                    <li>Anyone but an admin who opens the pages directly gets a 404.</li>
                </ul>
            </Panel>

            {grouped.map(({ group, rows }) => (
                <Panel key={group} title={group}>
                    <div className="flex flex-col gap-2">
                        {rows.map(row => (
                            <FeatureRow key={row.feature} row={row} saving={saving} onToggle={toggle} />
                        ))}
                    </div>
                </Panel>
            ))}
        </div>
    );
}

export default Features;
